import * as React from "react";
import { Form, useFormApi, FormApi, useFieldState } from "informedv4";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { Required, RequiredNotBlank } from "../../forms/validation/Required";
import Modal from "../../misc/components/modal";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { UnitType } from "webportalclient";
import { Checkbox } from "../../forms/informedv4/CheckboxV4";

export interface StudyCustomisationModalFormValues {
    name: string;
    displayText: string;
    format: { id: string, name: string };
    customFormat: string;
    minimumLength: number;
    maximumLength: number;
    unitType: UnitType;
    minimumValue: number;
    maximumValue: number;
    importance: { id: string, name: string };
    prefixWithSite: boolean;
    prefixDelimiter: string;
    isUnitTypeAvailable: boolean;
    isImportanceAvailable: boolean;
    isMinAndMaxLengthAvailable: boolean;
    isMinAndMaxValueAvailable: boolean;
    isDisplayTextAvailable: boolean;
    isPrefixAvailable: boolean;
}

interface StudyCustomisationModalProps {
    show: boolean;
    itemId: string;
    fieldPrefix: string;
    onClose: () => void;
    formatOptions: { id: string, name: string }[];
    importanceOptions: { id: string, name: string }[];
}

interface StudyCustomisationModalFormProps {
    itemId: string;
    fieldPrefix: string;
    formatOptions: { id: string, name: string }[];
    importanceOptions: { id: string, name: string }[];
}

export type FormApiFullDefinition<T> = FormApi & {
    getInitialValue: <V>(name: string) => V };


const StudyCustomisationModalForm = ({ itemId, fieldPrefix, formatOptions, importanceOptions }: StudyCustomisationModalFormProps) => {
    const formApi = useFormApi() as FormApiFullDefinition<StudyCustomisationModalFormValues>;
    const { unitTypes } = useReferenceContextProvider();
    const fieldFormatState = useFieldState(`${fieldPrefix}.format`);

    const fieldPrefixAdapter = fieldPrefix.replace("[", "_").replace("]", "_")

    let unitTypeCategory = undefined;
    switch (itemId) {
        case "SUBJECT_HEIGHT":
            unitTypeCategory = "MeasuredDistance";
            break;
        case "SUBJECT_WEIGHT":
            unitTypeCategory = "Weight";
            break;
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    {formApi.getInitialValue(`${fieldPrefix}.isDisplayTextAvailable`) ?
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor={`${fieldPrefixAdapter}displayText`}>Display Text</label>
                            <TextBox
                                name={`${fieldPrefix}.displayText`}
                                id={`${fieldPrefixAdapter}displayText`}
                                field={`${fieldPrefix}.displayText`}
                                validate={RequiredNotBlank}
                            />
                        </div> : null}
                    {formApi.getInitialValue(`${fieldPrefix}.isFormatAvailable`) ? <><div className="form-group col-md-6 col-sm-6 col-xs-12">
                        <label htmlFor={`${fieldPrefixAdapter}format`}>Format</label>
                        <MultiSelect
                            name={`${fieldPrefix}.format`}
                            id={`${fieldPrefixAdapter}format`}
                            field={`${fieldPrefix}.format`}
                            disabled={false}
                            options={formatOptions}
                            validate={Required}
                            labelKey="name"
                            valueKey="id"
                            multi={false}
                        /> 
                    </div>
                    <div className="form-group col-md-6 col-sm-6 col-xs-12">
                     <label htmlFor={`${fieldPrefixAdapter}customFormat`}>Custom Format</label>
                            <TextBox
                                name={`${fieldPrefix}.customFormat`}
                                id={`${fieldPrefixAdapter}customFormat`}
                                field={`${fieldPrefix}.customFormat`}
                                disabled={(formApi.getValue(`${fieldPrefix}.format`) as any)?.id !== "CUSTOM"}
                        />
                    </div></> : null}
                    {formApi.getInitialValue(`${fieldPrefix}.isImportanceAvailable`) ? <div className="form-group col-md-6 col-sm-6 col-xs-12">
                        <label htmlFor={`${fieldPrefixAdapter}importance`}>Importance</label>
                        <MultiSelect
                            name={`${fieldPrefix}.importance`}
                            id={`${fieldPrefixAdapter}importance`}
                            field={`${fieldPrefix}.importance`}
                            disabled={false}
                            options={importanceOptions}
                            validate={Required}
                            labelKey="name"
                            valueKey="id"
                            multi={false}
                        />
                    </div> : null}
                    {formApi.getInitialValue(`${fieldPrefix}.isMinAndMaxLengthAvailable`) ?
                        <>
                            <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                <label htmlFor={`${fieldPrefixAdapter}minimumLength`}>Minimum Length</label>
                                <TextBox
                                    name={`${fieldPrefix}.minimumLength`}
                                    id={`${fieldPrefixAdapter}minimumLength`}
                                    field={`${fieldPrefix}.minimumLength`}
                                    type="number"
                                    min="1"
                                    max={formApi.getInitialValue(`${fieldPrefix}.absoluteFieldLength`) as string ?? ""}
                                />
                            </div>
                            <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                <label htmlFor={`${fieldPrefixAdapter}maximumLength`}>Maximum Length</label>
                                <TextBox
                                    name={`${fieldPrefix}.maximumLength`}
                                    id={`${fieldPrefixAdapter}maximumLength`}
                                    field={`${fieldPrefix}.maximumLength`}
                                    type="number"
                                    min="1"
                                    max={formApi.getInitialValue(`${fieldPrefix}.absoluteFieldLength`) as string ?? ""}
                                />
                            </div>
                        </> : null}
                    {formApi.getInitialValue(`${fieldPrefix}.isMinAndMaxValueAvailable`) ?
                        <>
                            <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                <label htmlFor={`${fieldPrefixAdapter}minimumValue`}>Minimum Value</label>
                                <TextBox
                                    name={`${fieldPrefix}.minimumValue`}
                                    id={`${fieldPrefixAdapter}minimumValue`}
                                    field={`${fieldPrefix}.minimumValue`}
                                    type="number"
                                />
                            </div>
                            <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                <label htmlFor="format">Maximum Value</label>
                                <TextBox
                                    name={`${fieldPrefix}.maximumValue`}
                                    id={`${fieldPrefixAdapter}maximumValue`}
                                    field={`${fieldPrefix}.maximumValue`}
                                    type="number"
                                />
                            </div>
                        </> : null}
                    {formApi.getInitialValue(`${fieldPrefix}.isUnitTypeAvailable`) ? <div className="form-group col-md-6 col-sm-6 col-xs-12">
                        <label htmlFor={`${fieldPrefixAdapter}unitType`}>Unit Type</label>
                        <MultiSelect
                            name={`${fieldPrefix}.unitType`}
                            id={`${fieldPrefixAdapter}unitType`}
                            field={`${fieldPrefix}.unitType`}
                            disabled={false}
                            options={unitTypes.filter(x => x.dataItemTypeId === unitTypeCategory)}
                            validate={Required}
                            labelKey="name"
                            valueKey="id"
                            multi={false}
                        />
                    </div> : null}
                    {formApi.getInitialValue(`${fieldPrefix}.isPrefixAvailable`) ? <>
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor={`${fieldPrefixAdapter}prefixWithSite`}>Prefix with Site Number</label>
                            <Checkbox
                                name={`${fieldPrefix}.prefixWithSite`}
                                id={`${fieldPrefixAdapter}prefixWithSite`}
                                field={`${fieldPrefix}.prefixWithSite`}
                                label="Prefix"
                            />
                        </div>
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor={`${fieldPrefixAdapter}prefixDelimiter`}>Prefix Delimiter</label>
                            <TextBox
                                name={`${fieldPrefix}.prefixDelimiter`}
                                id={`${fieldPrefixAdapter}prefixDelimiter`}
                                field={`${fieldPrefix}.prefixDelimiter`}
                            />
                        </div>
                    </> : null}
                </div>
            </div>
        </>
    );
}

export const StudyCustomisationModal = ({ show, onClose, itemId, fieldPrefix, formatOptions, importanceOptions }: StudyCustomisationModalProps) => {
    return (
        <StudyCustomisationModalForm itemId={itemId} fieldPrefix={fieldPrefix} formatOptions={formatOptions} importanceOptions={importanceOptions} />
    );
}
